import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit, ViewChild, ViewContainerRef } from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { CompareHelper, ConvertHelper } from '@barthauer/abc/core';
import {
  CoreDataEntity,
  DataService,
  IPersistable,
  PersistenceService,
  PersistMode,
  Query,
  QueryColumnSortOrder,
  QueryObjectsModel,
} from '@barthauer/abc/core/data';
import { ResourceManager } from '@barthauer/abc/core/resources';
import {
  DialogResult,
  MessageBoxButtons,
  MessageBoxIcon,
  MessageBoxService,
  MessageMode,
  MessageSettings,
} from '@barthauer/abc/messages';
import {
  AbcContainerService,
  AbcNotificationComponent,
  AbcWindowComponent,
  DocActionButton,
  INotification,
  IWindow,
  ToastType,
  WindowStyle,
} from '@barthauer/abc/ui-components';
import { Observable, of, Subject, Subscription } from 'rxjs';
import { concatMap, map, takeUntil } from 'rxjs/operators';
import { DocInfo, DocumentModel } from '../../models/document.model';
import { DocumentsEditorsService } from '../../services/documents-editors.service';
import { DocumentsEditorCardDetailsComponent } from './documents-editor-card-details/documents-editor-card-details.component';

// Need to be moved to a cordova service

declare let window: any;
declare let cordova: any;

@Component({
  selector: 'abc-common-editors-documents-editor-card',
  templateUrl: './documents-editor-card.component.html',
  styleUrls: ['./documents-editor-card.component.scss'],
  standalone: false,
})
export class DocumentsEditorCardComponent implements OnInit, OnDestroy {
  private persistenceCategory = 'DocumentsEditorCardComponent';
  public documentsList: DocumentModel[] = [];
  public isIconsVisible: boolean = false;
  public documentExist: boolean = false;
  windowInstance: AbcWindowComponent;
  private unsubscribe$ = new Subject<void>();
  private subscriptions: Subscription[] = [];
  private _objectType: string | number;
  public showLoader: boolean;
  private get objectType() {
    return this._objectType;
  }
  private set objectType(ot) {
    this._objectType = ot;
  }
  public baseUrl: string;
  public buttons: DocActionButton[] = [];
  public res = ResourceManager.getResources('abc-common-editors-documents');
  public isAllowed: boolean = false;
  public static readonly downloadIcon = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40">
  <defs>
  <style>.open{fill:#193755;}</style>
  </defs>
  <g id="Ebene_2" data-name="Ebene 2">
  <g id="open_blue" data-name="open blue">
  <path id="open_arrow" data-name="open arrow" class="open" d="M35.56,35.56H4.44V4.44H16V0H0V40H40V24H35.56ZM24.44,0V4.44h8L10.58,26.29l3.13,3.13L35.56,7.58v8H40V0Z"/></g></g></svg>`;
  public static readonly loaderAnimation = `<svg version="1.1" id="Calque_1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px"
  width="100%" height="100%" viewBox="0 0 38 38" style="enable-background:new 0 0 38 38;" xml:space="preserve">
  <style type="text/css">
 .circle{fill:none;stroke:#193755;stroke-width:3;stroke-opacity:0.5;}
 .st0{fill:none;stroke:url(#SVGID_1_);stroke-width:3;stroke-miterlimit:10;}
  </style>
  <g>
  <g transform="translate(1 1)">
   <circle class="circle" cx="18" cy="18" r="18"/>
   <linearGradient id="SVGID_1_" gradientUnits="userSpaceOnUse" x1="18" y1="8.5" x2="37" y2="8.5">
     <stop  offset="9.498415e-03" style="stop-color:#193755"/>
     <stop  offset="0.4777" style="stop-color:#193755"/>
     <stop  offset="0.9991" style="stop-color:#193755"/>
     <stop  offset="1" style="stop-color:#193755"/>
   </linearGradient>
   <path class="st0" d="M36,18c0-9.9-8.1-18-18-18		">
  <animateTransform  accumulate="none" additive="replace" attributeName="transform" calcMode="linear" dur="1.3s" fill="remove" from="0 18 18" repeatCount="indefinite" restart="always" to="360 18 18" type="rotate">
       </animateTransform>
   </path>
 </g>
  </g>
  </svg>`;
  public ConvertHelper = ConvertHelper;
  private _parentId: string;
  @Input() isReadOnly: boolean;
  @Input()
  get parentId(): string {
    return this._parentId;
  }
  set parentId(id) {
    this._parentId = id;
    this.getDocuments().subscribe();
  }
  @ViewChild('abcWindowContainer', { read: ViewContainerRef }) abcWindow: ViewContainerRef;
  @ViewChild('abcNotification', { static: true, read: ViewContainerRef }) abcNotification: ViewContainerRef;
  @Input() set parentInfo(data: CoreDataEntity) {
    if (data.id) {
      this.isAllowed = true;
      this.parentId = data.id;
      this.objectType = data.objectType;
    } else {
      this.parentId = null;
      this.objectType = null;
    }
  }

  constructor(
    private coreDataService: DataService,
    private changeDetectorRef: ChangeDetectorRef,
    private abcContainer: AbcContainerService,
    private persistenceService: PersistenceService,
    private documentsEditorsService: DocumentsEditorsService,
    private messageBoxService: MessageBoxService,
    public iconRegistry: MatIconRegistry,
    public sanitizer: DomSanitizer,
  ) {
    iconRegistry.addSvgIcon('Datei', sanitizer.bypassSecurityTrustResourceUrl('assets/images/file.svg'));
    iconRegistry.addSvgIcon(
      'Feststellungsbescheid',
      sanitizer.bypassSecurityTrustResourceUrl('assets/images/file_fsb.svg'),
    );
    iconRegistry.addSvgIcon('Genehmigung', sanitizer.bypassSecurityTrustResourceUrl('assets/images/file_gen.svg'));
    iconRegistry.addSvgIcon('Bild', sanitizer.bypassSecurityTrustResourceUrl('assets/images/file_img.svg'));
    iconRegistry.addSvgIcon('Verknüpfung', sanitizer.bypassSecurityTrustResourceUrl('assets/images/file_link.svg'));
    iconRegistry.addSvgIcon('Dateipfad', sanitizer.bypassSecurityTrustResourceUrl('assets/images/file_path.svg'));
    iconRegistry.addSvgIcon('Video', sanitizer.bypassSecurityTrustResourceUrl('assets/images/file_video.svg'));
    iconRegistry.addSvgIcon(
      'Wasserrechtsbescheid',
      sanitizer.bypassSecurityTrustResourceUrl('assets/images/file_wrb.svg'),
    );
    iconRegistry.addSvgIcon('Dokument', sanitizer.bypassSecurityTrustResourceUrl('assets/images/file_random.svg'));
    iconRegistry.addSvgIcon('open_doc', sanitizer.bypassSecurityTrustResourceUrl('assets/images/open_blue.svg'));
    iconRegistry.addSvgIcon('edit_doc', sanitizer.bypassSecurityTrustResourceUrl('assets/images/edit.svg'));
    iconRegistry.addSvgIcon('delete_doc', sanitizer.bypassSecurityTrustResourceUrl('assets/images/DeleteDoc.svg'));
  }

  public ngOnInit(): void {
    this.documentsEditorsService.updateData$.subscribe((data) => {
      if (!data) return;

      const documentId = data.documentId;

      const objectKey: IPersistable = {
        id: documentId,
        objectType: 'Document',
        persistenceCategory: this.persistenceCategory,
        changesPersisted: (persistMode) => (persistMode = documentId ? PersistMode.Update : PersistMode.Insert),
        revertChanges: () => {},
      };

      if (!documentId) {
        this.persistenceService.addObjectForInsert(objectKey);
      }

      if (data.fileToUpload?.docDetails) {
        const fileToUpload = new DocInfo(data.fileToUpload.docDetails);
        this.baseUrl = data.fileToUpload.baseUrl;
        fileToUpload.Kind = this.documentsEditorsService.getFileType(fileToUpload.Designation, true);
        this.addObjectValue(objectKey, fileToUpload);
      }

      const mapFields = [
        { value: data.numberValue, key: 'Number' },
        { value: data.dataCarrierValue, key: 'Media' },
        { value: data.programPathValue, key: 'ProgramPath' },
        { value: data.validFromInputValue, key: 'DateStart' },
        { value: data.validUntilInputValue, key: 'DateEnd' },
        { value: data.designation, key: 'Designation' },
        { value: data.commentValue, key: 'Comment' },
      ];

      mapFields.forEach(({ value, key }) => {
        if (value) {
          this.addObjectValue(objectKey, new DocInfo({ [key]: value }));
        }
      });
    });

    this.documentsEditorsService.refreshDocumentList
      .asObservable()
      .pipe(concatMap(() => this.getDocuments()))
      .subscribe();
    this.documentsEditorsService.closeDialog$.subscribe(() => {
      if (this.windowInstance) this.windowInstance.closeDialog();
    });

    // this.subscriptions.push(this.documentsEditorsService.updateData$.subscribe(() => this.getDocuments()));

    this.documentsEditorsService.notification$.subscribe({
      next: (notification) => {
        const notifAttr: INotification = {
          id: 'error-notification',
          message: notification.message,
          type: notification.type,
          displayTime: notification.type === 'error' ? 8000 : 4000,
        };
        this.abcContainer.createComponent(this.abcNotification, AbcNotificationComponent, notifAttr).componentRef
          .instance;
      },
    });
  }

  private getDocuments(): Observable<DocumentModel[]> {
    this.showLoader = true;
    return this.coreDataService.executeReadObjectsQuery(this.prepareQueryDocuments()).pipe(
      takeUntil(this.unsubscribe$),
      concatMap((data) => {
        if (!CompareHelper.isEmptyObject(data)) {
          const docs: DocumentModel[] = ConvertHelper.arrayBufferToObjectArray(data);
          this.documentsList = [];
          docs.forEach((element: DocumentModel) => {
            const card = this.createDocumentView(element);
            this.documentsList.push(card);
          });
          this.documentExist = true;
        } else {
          this.documentExist = false;
        }
        this.showLoader = false;
        return of(this.documentsList);
      }),
    );
  }

  private createDocumentView(doc: DocumentModel): DocumentModel {
    const btnArray = new Array();

    const btnDelete = new DocActionButton();
    btnDelete.id = 'delete_doc';
    btnDelete.icon = 'delete_doc';
    if (this.isReadOnly) {
      btnDelete.visible = !this.isReadOnly;
    } else {
      btnDelete.visible = this.isIconsVisible;
    }
    btnDelete.click = () => {
      const settings = new MessageSettings();
      settings.mode = MessageMode.Question;
      settings.icon = MessageBoxIcon.Question;
      settings.button = MessageBoxButtons.YesNo;
      settings.message = this.res('DocumentsEditorCard-ConfirmDeleteMessage');
      this.messageBoxService.showMessageBox(settings).subscribe((result: DialogResult) => {
        if (DialogResult.Yes === result['result']) {
          this.deleteDocument(doc);
          this.messageBoxService.dialog.closeAll();
          this.documentsEditorsService.callPersistPendingChanges();
        } else {
          this.messageBoxService.dialog.closeAll();
        }
      });
    };
    btnArray.push(btnDelete);

    const btnEdit = new DocActionButton();
    btnEdit.id = 'edit_doc';
    btnEdit.icon = 'edit_doc';
    if (this.isReadOnly) {
      btnEdit.visible = !this.isReadOnly;
    } else {
      btnEdit.visible = this.isIconsVisible;
    }
    btnEdit.click = () => {
      this.detailDocument(doc);
    };
    btnArray.push(btnEdit);
    const btnOpenDoc = new DocActionButton();
    btnOpenDoc.id = 'open_doc';
    btnOpenDoc.icon = 'open_doc';
    btnOpenDoc.visible = true;
    btnOpenDoc.click = () => {
      this.downloadFile(doc);
    };
    btnArray.push(btnOpenDoc);

    doc.Buttons = btnArray;
    return doc;
  }
  private addObjectValue(objectKey: IPersistable, cardData: DocInfo): void {
    for (const property in cardData) {
      if (property == 'FileName') {
        this.persistenceService.addObjectValue(objectKey, 'Object', this.getBinaryStringFromUrl(this.baseUrl));
        this.persistenceService.addObjectValue(objectKey, 'FileName', cardData.FileName);
        this.persistenceService.addObjectValue(objectKey, 'Designation', cardData.Designation);
      } else {
        this.persistenceService.addObjectValue(objectKey, property, cardData[property]);
      }
    }
    this.persistenceService.addObjectValue(objectKey, 'ParentId', this.parentId);
    this.persistenceService.addObjectValue(objectKey, 'ParentObjectType', this.objectType);
  }

  private deleteDocument(data: DocumentModel): void {
    const objectKey: IPersistable = {
      id: data.Id,
      objectType: 'Document',
      persistenceCategory: this.persistenceCategory,
      changesPersisted: (persistMode: PersistMode) => {
        persistMode = PersistMode.Delete;
      },
      revertChanges: () => {},
    };
    this.persistenceService.addObjectForDelete(objectKey);
    this.RemoveElementFromStringArray(data);
  }

  private prepareQueryDocuments(): QueryObjectsModel {
    const queryDocument: QueryObjectsModel = new QueryObjectsModel();
    const mainQuery: Query = new Query();
    mainQuery.ObjectType = 'Document';
    mainQuery.OPath = `ParentId='${this.parentId}'`;
    mainQuery.Columns = [
      this.coreDataService.createQueryColumn('Id'),
      this.coreDataService.createQueryColumn('ObjectType'),
      this.coreDataService.createQueryColumn('Comment'),
      this.coreDataService.createQueryColumn('Designation', 'Designation', QueryColumnSortOrder.Ascending),
      this.coreDataService.createQueryColumn('Date'),
      this.coreDataService.createQueryColumn('FileName'),
      this.coreDataService.createQueryColumn('ref:Kind', 'Kind'),
      this.coreDataService.createQueryColumn('Number'),
      // this.coreDataService.createQueryColumn('Object', 'Object'),
      this.coreDataService.createQueryColumn('ProgramPath'),
      this.coreDataService.createQueryColumn('UpdateDate'),
      this.coreDataService.createQueryColumn('Media'),
      this.coreDataService.createQueryColumn('DateStart'),
      this.coreDataService.createQueryColumn('DateEnd'),
    ];
    queryDocument.ObjectQueries = [mainQuery];
    return queryDocument;
  }

  private queryBinaryObject(id: string) {
    const queryDocument: QueryObjectsModel = new QueryObjectsModel();
    const mainQuery: Query = new Query();
    mainQuery.ObjectType = 'Document';
    mainQuery.OPath = `Id='${id}'`;
    mainQuery.Columns = [
      this.coreDataService.createQueryColumn('Id', 'Id'),
      this.coreDataService.createQueryColumn('Date', 'Date'),
      this.coreDataService.createQueryColumn('FileName', 'FileName'),
      this.coreDataService.createQueryColumn('Designation', 'Designation'),
      this.coreDataService.createQueryColumn('ref:Kind', 'Kind'),
      this.coreDataService.createQueryColumn('Object', 'Object'),
    ];
    queryDocument.ObjectQueries = [mainQuery];
    return queryDocument;
  }

  private downloadFile(doc: DocumentModel) {
    const element = document.getElementById(doc.Id);
    const downloadIcon = element.querySelector('#open_doc');
    downloadIcon.innerHTML = DocumentsEditorCardComponent.loaderAnimation;
    if (!doc.FileName) {
      this.coreDataService.executeReadObjectsQuery(this.queryBinaryObject(doc.Id)).subscribe({
        next: (data) => {
          const _doc = new DocumentModel(data)[0];
          const url = `data:${this.getMimeType(_doc)};base64,${_doc.Object}`;
          this.createHrefAndDownload(url, _doc);
          downloadIcon.innerHTML = DocumentsEditorCardComponent.downloadIcon;
        },
        error: (err) => {
          this.documentsEditorsService.notificationSubject.next({ type: ToastType.Error, message: err.message });
        },
      });
    } else if (doc.FileName) {
      this.documentsEditorsService
        .readFilePath(doc.FileName)
        .pipe(
          map((path) => {
            const regExp = /^[A-Za-z]+:\/\//;

            if (regExp.test(path)) {
              try {
                const a = document.createElement('a');
                a.href = path;
                a.target = '_blank';
                a.click();
              } catch (error) {
                this.documentsEditorsService.notificationSubject.next({
                  type: ToastType.Error,
                  message: error.message,
                });
              } finally {
                downloadIcon.innerHTML = DocumentsEditorCardComponent.downloadIcon;
              }
              return null;
            } else {
              return path;
            }
          }),
          concatMap((path) => {
            if (path) {
              return this.documentsEditorsService.readFile(path);
            } else {
              return of(null);
            }
          }),
        )
        .subscribe({
          next: (res) => {
            if (res) {
              const fileReader = new FileReader();
              fileReader.readAsDataURL(res);
              fileReader.onload = () => {
                const blob = new Blob([res], { type: this.getMimeType(doc), endings: 'transparent' });
                const fileUrl = window.URL.createObjectURL(blob);
                this.createHrefAndDownload(fileUrl, doc);
                downloadIcon.innerHTML = DocumentsEditorCardComponent.downloadIcon;
              };
            }
          },
          error: (error: HttpErrorResponse) => {
            this.documentsEditorsService.notificationSubject.next({ type: ToastType.Error, message: error.message });
            downloadIcon.innerHTML = DocumentsEditorCardComponent.downloadIcon;
          },
        });
    }
  }

  private createHrefAndDownload(href, doc: DocumentModel) {
    if (window['deviceReady']) {
      this.checkDownloadPermission(doc, href);
      this.documentsEditorsService.notificationSubject.next({
        type: ToastType.Success,
        message: this.res('DocumentsEditorCard-DownloadSucces'),
      });
      this.changeDetectorRef.detectChanges();
    } else {
      let filename = doc.FileName?.split('\\').pop();
      if (filename === doc.FileName || (filename?.indexOf('.') ?? -1) < 0) {
        filename = doc.FileName?.split('/').pop();
      }
      if (filename === doc.FileName || (filename?.indexOf('.') ?? -1) < 0) {
        filename = doc.Designation;
      }
      if (!filename?.startsWith(doc.Designation) && doc.Designation.indexOf('.') < 0) {
        filename = doc.Designation + ' ' + filename;
      }
      const a = document.createElement('a');
      a.href = href;
      a.download = filename;
      a.click();
    }
  }

  private getMimeType(doc: DocumentModel, ext?: string) {
    const _extension = doc.Designation.split('.').pop();
    const extension = (ext || _extension).toLowerCase();
    switch (extension) {
      case 'png':
        return 'image/png';
      case 'jpg':
      case 'JPG':
        return 'image/jpeg';
      case 'pdf':
      case 'zip':
        return 'application/' + extension;
      case 'doc':
        return 'application/msword';
      case 'docx':
        return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      case 'ppt':
        return 'application/vnd.ms-powerpoint';
      case 'pptx':
        return 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
      case 'txt':
        return 'text/plain';
      case 'xls':
        return 'application/vnd.ms-excel';
      case 'xlsx':
        return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      case 'csv':
        return 'text/csv';
      case 'xml':
        return 'text/xml';
      default:
        const fileNameExt = doc.FileName?.split('.').pop();
        if (fileNameExt && doc.FileName?.indexOf('.') > -1 && fileNameExt !== ext) {
          return this.getMimeType(doc, fileNameExt);
        } else if (doc.Designation.indexOf('.') === -1) {
          if (doc.Kind) {
            switch (doc.Kind) {
              case 'Bild':
                return 'image/jpeg';
              default:
                return 'application/octet-stream';
            }
          }
        } else {
          return 'application/octet-stream';
        }
    }
  }

  public detailDocument(cardData?: DocumentModel): void {
    const style: WindowStyle = {
      width: '500px',
      height: 'auto',
      shading: true,
    };
    const WindowAttr: IWindow = {
      id: 'documents-Editor-Details',
      label: this.res('DocumentsEditorCard-Document'),
      minimizeButton: false,
      maximizeButton: false,
      component: DocumentsEditorCardDetailsComponent,
      setWindowStyle: (windowStyle: WindowStyle) => (windowStyle = style),
    };
    const window = this.abcContainer.createComponent(this.abcWindow, AbcWindowComponent, WindowAttr);
    this.windowInstance = <AbcWindowComponent>window.componentRef.instance;
    this.windowInstance.createComponentInstance().subscribe((activeComponent) => {
      const componentInstance = activeComponent.openWindow();
      const componentRef = <DocumentsEditorCardDetailsComponent>componentInstance.instance;
      activeComponent.onAfterOpened.subscribe(() => {
        componentRef.docInfo = cardData;
      });
    });
  }

  private getBinaryStringFromUrl(url: string): string {
    return url.split(',')[1];
  }

  public ngOnDestroy(): void {
    this.unsubscribe$.next(null);
    this.unsubscribe$.complete();
  }

  private RemoveElementFromStringArray(element: DocumentModel): void {
    this.documentsList.forEach((value, index) => {
      if (value === element) this.documentsList.splice(index, 1);
    });
  }

  public getDocumentFileName(doc: DocumentModel): string {
    return doc.FileName ? doc.FileName : this.res('DocumentsEditorCard-BinaryData');
  }
  // Need to be moved to a cordova service
  private checkDownloadPermission(doc: DocumentModel, url): void {
    const permissions = cordova.plugins.permissions;
    const permissionList = [permissions.WRITE_EXTERNAL_STORAGE, permissions.READ_EXTERNAL_STORAGE];
    permissions.checkPermission(permissions.WRITE_EXTERNAL_STORAGE, (status) => {
      if (status.hasPermission) {
        this.cordovaDownloadFile(doc, url);
      } else {
        permissions.requestPermissions(
          permissionList,
          (succes) => {
            this.cordovaDownloadFile(doc, url);
          },
          (error) => {
            console.log(error);
          },
        );
      }
    });
  }

  // Need to be moved to a cordova service
  private cordovaDownloadFile(doc: DocumentModel, url) {
    const isAndroid = navigator.userAgent.includes('Android');
    const xhr = new XMLHttpRequest();
    xhr.open('GET', url, true);
    xhr.responseType = 'blob';
    xhr.onload = async () => {
      if (xhr.status === 200) {
        const blob = xhr.response;
        const reader = new FileReader();
        reader.onload = () => {
          const fileType = this.getMimeType(doc);
          const extension = fileType.split('/').slice(-1)[0];
          const data = reader.result;
          const fileName = doc.Designation + new Date().getTime() + '.' + extension;

          if (isAndroid) {
            const fileSystem = window['requestFileSystem'] || window['webkitRequestFileSystem'];
            const targetPath = cordova.file.externalRootDirectory + 'Download/';
            window['resolveLocalFileSystemURL'](
              targetPath,
              async (dirEntry) => {
                fileSystem(
                  window['PERSISTENT'],
                  1024 * 1024,
                  (fs) => {
                    dirEntry.getFile(
                      fileName,
                      { create: true, exclusive: false },
                      (fileEntry) => {
                        fileEntry.createWriter((fileWriter) => {
                          fileWriter.onwriteend = () => {};
                          fileWriter.write(new Blob([data], { type: fileType }));
                        });
                      },
                      (error) => console.error(error),
                    );
                  },
                  (error) => console.error(error),
                );
              },
              (error) => console.error(error),
            );
          } else {
            const fileSystem = window['requestFileSystem'] || window['webkitRequestFileSystem'];
            fileSystem(
              window['PERSISTENT'],
              1024 * 1024,
              (fs) => {
                fs.root.getFile(
                  fileName,
                  { create: true, exclusive: false },
                  (fileEntry) => {
                    fileEntry.createWriter((fileWriter) => {
                      fileWriter.onwriteend = () => {};
                      fileWriter.write(new Blob([data], { type: fileType }));
                    });
                  },
                  (error) => console.error(error),
                );
              },
              (error) => console.error(error),
            );
          }
        };
        reader.readAsArrayBuffer(blob);
      }
    };
    xhr.send();
  }
  public tryGetDocumentType(doc: DocumentModel): string {
    let kind: string = doc?.Kind;

    if (!kind && doc.FileName?.indexOf('.') > -1) {
      const fileNameExt: string = doc.FileName.split('.').pop()?.toLowerCase();
      switch (fileNameExt) {
        case 'png':
        case 'jpg':
          kind = 'Bild';
          break;
        case 'docx':
        case 'doc':
          kind = 'Dokument';
          break;
        default:
          kind = 'Datei';
      }
    }

    return kind || 'Datei';
  }
}
